import { useCallback, useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  TopBar,
  AppMenu,
  Button,
  Spinner,
  NotificationAlert,
  NotificationBanner,
  NotificationToast,
  IconButton,
  Icons,
  Paragraph,
  useAddToast,
  type AppMenuOptions,
} from '@hm-group/bads-react';
import { environment } from '../../environments/environment';
import type { UserProfile } from '../../models';
import { httpGet } from '../../services/httpService';
import { onHttpError, suppressNextError } from '../../services/httpErrorBus';
import { useAppContext } from '../../context/AppContext';
import { useAppPolling } from '../../hooks/useAppPolling';
import { SearchBar } from '../SearchBar/SearchBar';
import { PlanningView } from '../PlanningView/PlanningView';
import { MaintenanceBanner } from '../MaintenanceBanner/MaintenanceBanner';
import { MaintenancePage } from '../MaintenancePage/MaintenancePage';
import { LS, lsGet } from '../../constants/constants';
import styles from './Home.module.css';

import {
  AssortmentCanvasBackgroundNormal,
  PPlanBackgroundNormal,
  AssortmentOverviewBackgroundNormal,
  AssortmentSupportLearningBackgroundNormal,
} from '@hm-group/dap-symbols';

const appMenuOptions: AppMenuOptions[] = [
  {
    title: 'Apps',
    options: [
      {
        label: 'Assortment Canvas',
        onClick: () => globalThis.open(environment.externalApps.assortmentCanvas, '_blank', 'noopener,noreferrer'),
        icon: AssortmentCanvasBackgroundNormal,
      },
      {
        label: 'Assortment Overview',
        onClick: () => globalThis.open(environment.externalApps.assortmentOverview, '_blank', 'noopener,noreferrer'),
        icon: AssortmentOverviewBackgroundNormal,
      },
      {
        label: 'Product Plan',
        onClick: () => globalThis.open(environment.externalApps.productPlan, '_blank', 'noopener,noreferrer'),
        icon: PPlanBackgroundNormal,
      },
    ],
  },
  {
    title: 'More',
    options: [
      {
        label: 'Assortment Support Learning',
        onClick: () => globalThis.open(environment.externalApps.assortmentSupportLearning, '_blank', 'noopener,noreferrer'),
        icon: AssortmentSupportLearningBackgroundNormal,
      },
    ],
  },
];

/** Subscribes to the HTTP error bus and shows a BADS error toast for every unhandled API error. */
function HttpErrorToastListener() {
  const { addToast } = useAddToast();
  useEffect(() => onHttpError((message, timeout) => {
    addToast({ title: 'Error', subtitle: message, type: 'error', timeout: timeout ?? 5000 });
  }), [addToast]);
  return null;
}

export function Home() {
  const { accounts } = useMsal();
  const {
    enablePublishBtn,
    is403Error,
    gridSelectionData,
    triggerPublish,
    searchBarData,
  } = useAppContext();

  const [userName, setUserName] = useState('');
  const [httpCallInProgress] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [hasCheckedInitialLoad, setHasCheckedInitialLoad] = useState(false);
  const [selectedView, setSelectedView] = useState<'department' | 'section'>('department');
  const isLoggedIn = accounts.length > 0;

  const { newVersionAvailable, maintenanceConfig, maintenanceStatus } = useAppPolling();

  const isMaintenanceBlocked =
    maintenanceStatus?.isMaintenanceMode === true &&
    maintenanceStatus.userHasAdminAccess !== true;

  const handleCountdownComplete = useCallback(() => {
    /* Polling will pick up the transition automatically on its next tick */
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    suppressNextError(); // Profile fetch has a graceful fallback – no toast needed
    httpGet<UserProfile>(environment.apiConfig.graph_api.uri)
      .then((prof) => {
        setUserName(`${prof.givenName ?? ''} ${prof.surname ?? ''}`.trim());
      })
      .catch(() => {
        const account = accounts[0];
        setUserName(account?.name ?? account?.username?.split('@')[0] ?? '');
      });
  }, [accounts, isLoggedIn]);

  // Auto-close sidebar on first load if organization is selected
  useEffect(() => {
    if (hasCheckedInitialLoad) return;

    // Check both searchBarData (from context) and localStorage (immediately available)
    const hasOrganizationFromContext = searchBarData.brand ||
      searchBarData.division ||
      searchBarData.section ||
      (searchBarData.departments && searchBarData.departments.length > 0);

    const hasOrganizationFromStorage = lsGet(LS.brand) ||
      lsGet(LS.division) ||
      lsGet(LS.section) ||
      lsGet(LS.department);

    if (hasOrganizationFromContext || hasOrganizationFromStorage) {
      setSidebarOpen(false);
    }

    setHasCheckedInitialLoad(true);
  }, [searchBarData, hasCheckedInitialLoad]);

  const handleReload = () => {
    globalThis.location.href = '/';
  };

  let mainContentBlock: React.ReactNode;
  if (isMaintenanceBlocked) {
    mainContentBlock = <MaintenancePage message={maintenanceStatus?.message} />;
  } else if (is403Error) {
    mainContentBlock = (
      <div className={styles.errorContent}>
        <Icons.ErrorOutline
          width="100"
          height="100"
          style={{ marginTop: '5%' }}
        />
        <div style={{
          marginRight: '38px',
          marginLeft: '38px',
          marginBottom: '10%',
          textAlign: 'center',
        }}>
          <Paragraph type="semi-bold">
            You are not having access to Article Plan. Please contact ASL
            (AssortmentSupportandLearning@hm.com) for access. Click here to
            reload{' '}
            <span style={{ verticalAlign: 'middle', display: 'inline-block' }}>
              <IconButton
                mode="primary"
                size="normal"
                onClick={handleReload}
              >
                <Icons.RestartAlt />
              </IconButton>
            </span>
          </Paragraph>
        </div>
      </div>
    );
  } else {
    mainContentBlock = (
      <div className={styles.mainContent}>
        {sidebarOpen && (
          <div className={styles.sidebarArea}>
            <div className={styles.sidebarHeader}>
              <span>Search Filters</span>
              <IconButton
                mode="secondary"
                size="small"
                onClick={() => setSidebarOpen(false)}
                aria-label="Close sidebar"
              >
                <Icons.Close />
              </IconButton>
            </div>
            <SearchBar />
          </div>
        )}
        <div className={styles.contentArea}>
          <PlanningView
            onSelectedViewChange={setSelectedView}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <TopBar sticky={true}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AppMenu
            options={appMenuOptions}
            testId="app-menu-article-plan"
          />
          {!sidebarOpen && (
            <IconButton
              mode="secondary"
              size="normal"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open search filters"
            >
              <Icons.Menu />
            </IconButton>
          )}
        </div>
        <div style={{ marginLeft: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3 style={{ color: 'white', margin: 0, fontSize: '1.5rem' }}>AP</h3>
          <span style={{ color: 'white', fontWeight: '600' }}>Article Plan</span>
        </div>
        <div className={styles.controlArea}>
          {isLoggedIn && (
            <div className={styles.profileDisplay}>
              <Icons.AccountCircle className={styles.profileIcon} />
              <span className={styles.profileName}>{userName}</span>
            </div>
          )}
        </div>
      </TopBar>

      <div className={`${styles.statusMessageArea} ${styles.flexRow} ${styles.alignCenter} ${styles.justifyCenter}`}>
        {httpCallInProgress > 0 && (
          <>
            <Spinner size="small" />
            <p style={{ color: 'darkgray', fontSize: '12px', fontWeight: 500, marginLeft: '8px' }}>
              loading...
            </p>
          </>
        )}
      </div>

      <HttpErrorToastListener />
      <NotificationToast yOffset="3.5rem" />
      <NotificationAlert />

      {maintenanceConfig.show && (
        <MaintenanceBanner config={maintenanceConfig} onCountdownComplete={handleCountdownComplete} />
      )}

      {newVersionAvailable && (
        <NotificationBanner
          type="information"
          title="A new version of Article Plan is available."
          showCloseIcon={false}
          noMargin
        >
          <Button mode="primary" size="small" onClick={() => globalThis.location.reload()}>
            Refresh
          </Button>
        </NotificationBanner>
      )}

      {mainContentBlock}

      <div className={styles.footer}>
        {enablePublishBtn && selectedView !== 'section' && (
          <Button
            size="normal"
            mode="primary"
            onClick={() => triggerPublish()}
            leftIcon={<Icons.FileUpload />}
          >
            Publish
          </Button>
        )}
        {gridSelectionData.cellsCount > 0 && (
          <div className={styles.selectionDisplay}>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Selected: {gridSelectionData.cellsCount} cells | Sum:{' '}
              {gridSelectionData.sum.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}